// Admin → Integrations → npm. Multi-slot (org can configure several
// npm scopes / registry entries). Used by the editor's project-publish
// flow + the CI pipeline.
//
// Until the npm-publish edge function lands, this page only stores
// config; server-side publishes read (scope, registry, token) and
// assemble a valid .npmrc on the fly.

import {
  loadIntegrationCatalogue,
  saveIntegration,
  deleteIntegration
} from '../../integrations/index.js'
// resolveScope is a shared-library function; invoke via el.call('resolveScope', draft, defaultScopeType).

const KIND = 'npm'
const DEFAULT_SCOPE = 'org'

const _loadInstances = async (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    if (!root._npmHubWatcher) {
      root._npmHubWatcher = setInterval(() => {
        if (root?.jwtClaims?.workspace_org_id) {
          clearInterval(root._npmHubWatcher)
          root._npmHubWatcher = null
          _loadInstances(root)
        }
      }, 300)
    }
    return
  }
  const data = await loadIntegrationCatalogue(orgId)
  root.update({ _integrationHub: data })
}

const _slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '')
    .slice(0, 40) || 'default'

export const adminIntegrationsNpm = {
  extends: 'AppPage',
  metadata: { title: 'npm / Integrations / Symbols Workspace' },
  onInit: (el, s) => {
    if (!el.call('shouldRedirectNonAdmin', s)) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },
  onCreate: (el, s) => _loadInstances(s.root),

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: {
        BackLink: {
          tag: 'button', type: 'button',
          padding: '0', background: 'transparent', border: 'none',
          color: 'caption', fontSize: 'Z2', cursor: 'pointer',
          text: '← Integrations',
          onClick: (e, el, s) => {
            const root = el.call('findRouteRoot', el)
            if (root) el.router('/admin/integrations', root)
          }
        },
        Title: { text: 'npm registry' }
      },
      RightGroup: {
        AddBtn: {
          tag: 'button', type: 'button',
          padding: 'Z A', round: 'Y',
          background: 'blue', color: 'white',
          fontSize: 'Z2', fontWeight: '700',
          cursor: 'pointer', border: 'none',
          text: '+ Add scope',
          onClick: (e, el, s) => {
            s.root.update({
              _newNpmDraft: {
                slug: '', displayName: '', scope: '',
                registryUrl: 'https://registry.npmjs.org',
                alwaysAuth: 'true',
                token: ''
              },
              _npmFormOpen: true
            })
          }
        }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      Hint: {
        tag: 'p', fontSize: 'Z2', color: 'caption', margin: '0',
        text: 'Store one publish token per scope. Editor CMS reads these when exporting/publishing projects; CI reads them when running `npm publish`.'
      },

      NewForm: { extends: 'NpmNewForm' },
      Instances: { extends: 'NpmInstanceList' }
    }
  }
}

export const NpmNewForm = {
  hide: (el, s) => !s.root?._npmFormOpen,
  flow: 'y',
  gap: 'Z1',
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px', borderStyle: 'solid', borderColor: 'blue',
  padding: 'A',

  Title: {
    tag: 'h3', fontSize: 'A', fontWeight: '700', color: 'title', margin: '0',
    text: 'Add npm scope'
  },

  Fields: {
    flow: 'y',
    gap: 'Z1',
    childExtends: 'NpmField',
    childrenAs: 'state',
    children: () => [
      { label: 'Display name', placeholder: '@symbo.ls prod token', draftKey: 'displayName' },
      { label: 'Slug', placeholder: 'symbols-prod', draftKey: 'slug' },
      { label: 'Scope (@org — required)', placeholder: '@symbo.ls', draftKey: 'scope' },
      { label: 'Registry URL', placeholder: 'https://registry.npmjs.org', draftKey: 'registryUrl' }
    ]
  },

  ScopePicker: {
    extends: 'IntegrationScopePicker',
    draftKey: '_newNpmDraft',
    defaultScopeType: DEFAULT_SCOPE
  },

  Token: {
    flow: 'y', gap: 'V',
    Label: {
      tag: 'label', fontSize: 'Y2', color: 'caption', fontWeight: '700',
      text: 'npm token (required — written to Vault)'
    },
    Textarea: {
      tag: 'textarea', rows: '2',
      width: '100%', padding: 'Z', round: 'Y',
      background: 'document',
      borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
      color: 'title', fontFamily: 'mono', fontSize: 'Z2',
      placeholder: 'npm_...',
      onInput: (e, el, s) => {
        s.root.update({
          _newNpmDraft: {
            ...(s.root?._newNpmDraft || {}),
            token: e.target.value
          }
        })
      }
    },
    Hint: {
      tag: 'p', fontSize: 'Y2', color: 'caption', margin: '0',
      text: 'Create with `npm token create --type=automation --cidr=0.0.0.0/0`. Scope tokens allow publishing to a specific org.'
    }
  },

  Actions: {
    flow: 'x', gap: 'Z', align: 'center flex-end', marginTop: 'Z',

    CancelBtn: {
      tag: 'button', type: 'button',
      padding: 'V Z', round: 'Y',
      background: 'transparent',
      borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
      color: 'title', cursor: 'pointer', fontSize: 'Y2',
      text: 'Cancel',
      onClick: (e, el, s) => {
        s.root.update({ _npmFormOpen: false, _newNpmDraft: null })
      }
    },

    SaveBtn: {
      tag: 'button', type: 'button',
      padding: 'V A', round: 'Y',
      background: 'blue', color: 'white',
      fontSize: 'Y2', fontWeight: '700',
      cursor: 'pointer', border: 'none',
      text: 'Save',
      onClick: async (e, el, s) => {
        const orgId = s.root?.jwtClaims?.workspace_org_id
        const draft = s.root?._newNpmDraft || {}
        if (!orgId) return
        if (!draft.scope || !draft.token) {
          el.call('openNotification', { title: 'Scope and token required', type: 'error' })
          return
        }
        const slug = _slugify(draft.slug || draft.scope.replace(/^@/, ''))
        const scope = el.call('resolveScope', draft, DEFAULT_SCOPE)
        if (scope.scopeType !== 'org' && !scope.scopeId) {
          el.call('openNotification', {
            title: 'Scope target required',
            message: `Select a ${scope.scopeType} or switch scope to Org.`, type: 'error'
          })
          return
        }
        const { ok, body } = await saveIntegration({
          orgId, kind: KIND, slug,
          scopeType: scope.scopeType, scopeId: scope.scopeId,
          displayName: draft.displayName || draft.scope,
          secret: draft.token,
          config: {
            scope: draft.scope,
            registry_url: draft.registryUrl || 'https://registry.npmjs.org',
            always_auth: draft.alwaysAuth || 'true',
            display_name: draft.displayName || draft.scope
          },
          enabled: true
        })
        if (ok) {
          s.root.update({ _npmFormOpen: false, _newNpmDraft: null })
          await _loadInstances(s.root)
          el.call('openNotification', { title: 'Scope added', type: 'success' })
        } else {
          el.call('openNotification', {
            title: 'Save failed',
            message: body?.error || 'Server error',
            type: 'error'
          })
        }
      }
    }
  }
}

export const NpmField = {
  flow: 'y', gap: 'V',
  Label: {
    tag: 'label', fontSize: 'Y2', color: 'caption', fontWeight: '700',
    text: (el, s) => s.label
  },
  Input: {
    tag: 'input', type: 'text',
    width: '100%', padding: 'Z', round: 'Y',
    background: 'document',
    borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
    color: 'title', fontSize: 'Z2',
    placeholder: (el, s) => s.placeholder || '',
    value: (el, s) => {
      const draft = s.root?._newNpmDraft || {}
      return draft[s.draftKey] || ''
    },
    onInput: (e, el, s) => {
      s.root.update({
        _newNpmDraft: {
          ...(s.root?._newNpmDraft || {}),
          [s.draftKey]: e.target.value
        }
      })
    }
  }
}

export const NpmInstanceList = {
  flow: 'y', gap: 'A',
  childExtends: 'NpmInstanceCard',
  childrenAs: 'state',
  children: (el, s) => {
    const list = s.root?._integrationHub?.integrations || []
    return list.filter((i) => i.kind === KIND)
  },
  EmptyState: {
    hide: (el, s) => {
      const list = s.root?._integrationHub?.integrations || []
      return list.filter((i) => i.kind === KIND).length > 0
    },
    padding: 'B', round: 'Z1', background: 'overlay',
    borderWidth: '1px', borderStyle: 'dashed', borderColor: 'line',
    textAlign: 'center', color: 'caption', fontSize: 'Z2',
    text: 'No npm scopes configured. Add one to unlock publish in the editor and CI.'
  }
}

export const NpmInstanceCard = {
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
  padding: 'A',
  flow: 'x', align: 'center space-between', gap: 'Z',

  Left: {
    flow: 'x', align: 'center', gap: 'Z1',
    Icon: { icon: 'npm', boxSize: 'A' },
    Info: {
      flow: 'y', gap: 'V',
      Name: {
        tag: 'span', fontSize: 'A', fontWeight: '700', color: 'title',
        text: (el, s) => s.display_name || s.config?.scope || s.slug
      },
      Meta: {
        tag: 'span', fontSize: 'Y2', color: 'caption',
        text: (el, s) => `scope: ${s.config?.scope || '—'} · registry: ${s.config?.registry_url || 'default'}`
      }
    }
  },

  Right: {
    flow: 'x', gap: 'Z',

    StatusPill: {
      tag: 'span', padding: 'V Z', round: 'Y',
      fontSize: 'Y1', fontWeight: '700',
      background: (el, s) => (s.enabled && s.has_secret ? 'green' : 'overlayStrong'),
      color: (el, s) => (s.enabled && s.has_secret ? 'white' : 'caption'),
      text: (el, s) => {
        if (!s.has_secret) return 'Missing token'
        return s.enabled ? 'Live' : 'Disabled'
      }
    },

    RemoveBtn: {
      tag: 'button', type: 'button',
      padding: 'V Z', round: 'Y',
      background: 'transparent',
      borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
      color: 'red', fontSize: 'Y2', cursor: 'pointer',
      text: 'Remove',
      onClick: async (e, el, s) => {
        if (!window.confirm(`Remove ${s.display_name || s.slug}? Publish from this scope will fail until re-added.`)) return
        const orgId = s.root?.jwtClaims?.workspace_org_id
        const { ok } = await deleteIntegration({
          orgId, kind: KIND, slug: s.slug,
          scopeType: s.scope_type || 'org', scopeId: s.scope_id || null
        })
        if (ok) {
          await _loadInstances(s.root)
          el.call('openNotification', { title: 'Removed', type: 'success' })
        }
      }
    }
  }
}
